import type { ReactNode } from 'react';
import { useEffect, useEffectEvent, useId, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Paper } from '@mui/material';

import Typography from '@/components/Typography';
import { useOptionalAccordion } from '@/features/DataHub/components/ExportDrawer/AccordionContext';

export const DataHubCard = ({
  children,
  title,
  subtitle,
  renderRight,
  icon,
  expandable,
  defaultExpanded = false,
  collapsedSummary,
  darkBg = false
}: {
  children?: ReactNode;
  title: ReactNode;
  subtitle?: string;
  renderRight?: ReactNode;
  icon?: ReactNode;
  expandable?: boolean;
  defaultExpanded?: boolean;
  collapsedSummary?: ReactNode;
  darkBg?: boolean;
}) => {
  const id = useId();
  const accordion = useOptionalAccordion();
  const [localExpandedId, setLocalExpandedId] = useState<string | null>(null);
  const expandedId = accordion?.expandedId ?? localExpandedId;
  const setExpandedId = accordion?.setExpandedId ?? setLocalExpandedId;
  const expanded = useMemo(() => expandedId === id, [expandedId, id]);

  const header = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flex: 1
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          component='div'
          size={16}
          sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          {icon} {title}
          {!expanded && collapsedSummary != null && (
            <>
              :
              <Box
                component='span'
                sx={{ fontWeight: 400 }}
              >
                {collapsedSummary}
              </Box>
            </>
          )}
        </Typography>
      </Box>
      {renderRight && <Box>{renderRight}</Box>}
    </Box>
  );

  const checkForDefaultExpanded = useEffectEvent(() => {
    if (defaultExpanded) {
      setExpandedId(id);
    }
  });

  useEffect(() => {
    checkForDefaultExpanded();
  }, []);

  if (expandable) {
    return (
      <Accordion
        disableGutters
        expanded={expanded}
        sx={{
          p: 0,
          py: '8px',
          flex: 1,
          '&:before': {
            display: 'none'
          },
          boxShadow: 'none'
        }}
        onChange={() => {
          setExpandedId(prevId => (prevId === id ? null : id));
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{header}</AccordionSummary>
        <AccordionDetails
          sx={{
            pt: 0
          }}
        >
          {subtitle && (
            <Typography
              size={12}
              sx={{ fontWeight: 500, color: 'text.subtext', mb: '8px' }}
            >
              {subtitle}
            </Typography>
          )}
          {children}
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: darkBg ? 'background.default' : 'background.paper'
      }}
    >
      <Box sx={{ mb: children ? 2 : 0 }}>{header}</Box>
      {subtitle && (
        <Typography
          size={12}
          sx={{ fontWeight: 500, color: 'text.subtext', mb: '8px' }}
        >
          {subtitle}
        </Typography>
      )}
      {children}
    </Paper>
  );
};
